/**
 * A wrapping row of chips with a real overflow disclosure.
 *
 * The `+n` is rendered here rather than by each pillar, and it OPENS: an
 * overflow marker that is not a control is a count of things a reader cannot
 * reach, which is the accounting-table defect wearing a pill. Expanding
 * reveals the full wrapped set in place — no popover, no navigation.
 */
"use client";
import { useCallback, useState, type ReactElement } from "react";
import { Cbutton } from "@/components/ui/Cbutton";
import { ButtonPreset } from "@/constants/componentPresets";
import { CRC_R_PILL } from "./cresultcard.tokens";
import {
  CresultcardChipRegister,
  CresultcardChipVariant,
} from "./cresultcard.types";
import { CresultcardChip } from "./CresultcardChip";

const CHIP_GAP = 5;
const OVERFLOW_PREFIX = "+";
/** Matches the attribute register's own type size, so the row stays level. */
const OVERFLOW_TEXT_SIZE = 10;
const OVERFLOW_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/ChipRow/OverflowTrigger",
  "data-crc-control": true,
};

export interface CresultcardChipSpec {
  readonly key: string;
  readonly label: string;
  readonly variant?: CresultcardChipVariant;
  readonly title?: string;
}

interface CresultcardChipRowProps {
  readonly chips: readonly CresultcardChipSpec[];
  readonly register: CresultcardChipRegister;
  readonly wrap?: boolean;
  /** How many chips are shown before the overflow control. */
  readonly limit?: number;
  /** The first chip carries the row's distinguishing fact, so it can lead. */
  readonly emphasiseFirst?: boolean;
  readonly seeAllLabel: string;
}

export function CresultcardChipRow({
  chips,
  register,
  wrap = true,
  limit,
  emphasiseFirst = false,
  seeAllLabel,
}: CresultcardChipRowProps): ReactElement | null {
  const [expanded, setExpanded] = useState(false);
  const expand = useCallback((): void => setExpanded(true), []);

  if (chips.length === 0) {
    return null;
  }

  const visible = limit == null || expanded ? chips : chips.slice(0, limit);
  const overflow = chips.length - visible.length;

  return (
    <div
      data-el="Cresultcard/ChipRow/Root"
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: wrap ? "wrap" : "nowrap",
        gap: CHIP_GAP,
        minWidth: 0,
      }}
    >
      {visible.map((chip, index) => (
        <CresultcardChip
          key={chip.key}
          register={register}
          title={chip.title}
          variant={
            emphasiseFirst && index === 0
              ? CresultcardChipVariant.Accent
              : chip.variant
          }
        >
          {chip.label}
        </CresultcardChip>
      ))}
      {overflow > 0 && (
        <Cbutton
          ariaLabel={seeAllLabel}
          disableClickMotion
          label={`${OVERFLOW_PREFIX}${String(overflow)}`}
          onClick={expand}
          padding="3px 9px"
          preset={ButtonPreset.Ghost}
          radius={CRC_R_PILL}
          rootAttributes={{
            ...OVERFLOW_ROOT_ATTRIBUTES,
          }}
          textSize={OVERFLOW_TEXT_SIZE}
        />
      )}
    </div>
  );
}
